// src/components/WM.js

import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from "react";

import { normalizeId } from "../data/normalizeId";

import {
  buildVisitedSet,
  buildVisitedCSS,
  buildHighlightCSS
} from "./MapColoring";

const WM = forwardRef(
  ({ SVGMap, svgClass, visitedList, onRegionClick }, ref) => {
    const mapRef = useRef(null);
    const [highlighted, setHighlighted] = useState(null);
    const [rawSvg, setRawSvg] = useState(null);

    // ⭐ Store highlight request if SVG not ready yet
    const [pendingHighlight, setPendingHighlight] = useState(null);

    // ⭐ Allow MapPage to call highlightPlace() from search bar
    useImperativeHandle(ref, () => ({
      highlightPlace: (name) => {
        if (!name) {
          setHighlighted(null);
          return;
        }

        const svg = mapRef.current;
        const normalized = normalizeId(name);

        if (!svg) {
          setPendingHighlight(normalized);
          return;
        }

        let path = svg.querySelector(`#${normalized}`);
        if (!path) {
          path = Array.from(svg.querySelectorAll("path")).find((p) => {
            const candidate = normalizeId(p.getAttribute("data-name") || p.getAttribute("data-id") || p.id);
            return candidate === normalized;
          });
        }

        // ⭐ If SVG not ready → store request
        if (!path) {
          setPendingHighlight(normalized);
          return;
        }

        const svgId = path.id || normalizeId(path.getAttribute("data-id") || name);
        setHighlighted(svgId);

        const pretty = path.getAttribute("data-name") || path.getAttribute("data-id") || name;
        const normalizedPretty = normalizeId(pretty);
        const normalizedSvgId = normalizeId(svgId);

        // ⭐ UPDATED: check both categories and fallback to SVG id
        const isVisited =
          visitedList.withVideo?.includes(normalizedPretty) ||
          visitedList.noVideo?.includes(normalizedPretty) ||
          visitedList.withVideo?.includes(normalizedSvgId) ||
          visitedList.noVideo?.includes(normalizedSvgId);

        if (!isVisited) return;

        if (typeof onRegionClick === "function") {
          onRegionClick(svgId);
        }
      }
    }));

    // ⭐ Detect if SVGMap is a URL → fetch raw SVG text
    useEffect(() => {
      setRawSvg(null);
      if (typeof SVGMap === "string") {
        fetch(SVGMap)
          .then((res) => res.text())
          .then((text) => setRawSvg(text));
      }
    }, [SVGMap]);

    // ⭐ Clear highlight whenever the map changes
    useEffect(() => {
      setHighlighted(null);
    }, [SVGMap]);

    // ⭐ Build visited set + CSS (supports withVideo + noVideo)
    const visitedSet = buildVisitedSet(visitedList);
    const visitedCSS = buildVisitedCSS(visitedSet, svgClass);

    // ⭐ Apply pending highlight when URL-based SVG loads (Puerto Rico)
    useEffect(() => {
      if (!pendingHighlight) return;

      const svg = mapRef.current;
      if (!svg) return;

      const path = svg.querySelector(`#${pendingHighlight}`);
      if (path) {
        setHighlighted(path.id);
        setPendingHighlight(null);
      }
    }, [rawSvg, pendingHighlight]);

    // ⭐ Apply pending highlight when component-based SVG mounts (World, USA)
    useEffect(() => {
      if (!pendingHighlight) return;

      const id = setTimeout(() => {
        const svg = mapRef.current;
        if (!svg) return;

        const path = svg.querySelector(`#${pendingHighlight}`);
        if (path) {
          setHighlighted(path.id);
          setPendingHighlight(null);
        }
      }, 0);

      return () => clearTimeout(id);
    }, [SVGMap, pendingHighlight]);

    // ⭐ Attach click handlers
    useEffect(() => {
      const svg = mapRef.current;
      if (!svg) return;

      const paths = svg.querySelectorAll("path");

      const handleClick = (e) => {
        const target = e.target.closest("path");
        if (!target) return;

        const prettyName = target.getAttribute("data-name");
        const svgId = target.id || target.getAttribute("data-id");

        const normalizedPretty = normalizeId(prettyName || svgId);
        const normalizedSvgId = normalizeId(svgId);

        // ⭐ USA override
        if (["unitedstates", "usa", "us"].includes(normalizedSvgId)) {
          if (typeof onRegionClick === "function") {
            onRegionClick(svgId);
          }
          return;
        }

        // ⭐ UPDATED: check both categories and fallback to SVG id
        const isVisited =
          visitedList.withVideo?.includes(normalizedPretty) ||
          visitedList.noVideo?.includes(normalizedPretty) ||
          visitedList.withVideo?.includes(normalizedSvgId) ||
          visitedList.noVideo?.includes(normalizedSvgId);

        if (isVisited) {
          if (typeof onRegionClick === "function") {
            onRegionClick(svgId);
          }
          return;
        }

        setHighlighted(svgId || normalizedPretty);
      };

      paths.forEach((p) => p.addEventListener("click", handleClick));
      return () =>
        paths.forEach((p) => p.removeEventListener("click", handleClick));
    }, [visitedList, onRegionClick, rawSvg]);

    return (
      <div className="map-container">
        {typeof SVGMap === "string" ? (
          rawSvg && (
            <div
              ref={mapRef}
              className={`${svgClass} map-svg`}
              dangerouslySetInnerHTML={{ __html: rawSvg }}
            />
          )
        ) : (
          <SVGMap
            ref={mapRef}
            className={`${svgClass} map-svg`}
          />
        )}

        <style>{visitedCSS}</style>

        {highlighted && (
          <style>{buildHighlightCSS(highlighted, svgClass)}</style>
        )}
      </div>
    );
  }
);

export default WM;
